import {
  AlertTriangle,
  BarChart3,
  BookOpen,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleDashed,
  ClipboardCheck,
  Clock3,
  FileText,
  Minus,
  Plus,
  RefreshCw,
  Save,
  Send,
  ShieldCheck,
  TrendingUp,
  UserRound,
  Users,
  X
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import {
  ApiRequestError,
  createUser,
  getDashboard,
  getRules,
  getSession,
  listUsers,
  saveTodayReport,
  updateRules,
  updateUser
} from "./api";
import type {
  ApiError,
  DashboardPayload,
  PublicUser,
  Report,
  ReportInput,
  ReportMetrics,
  Role,
  SessionPayload,
  UserStatus
} from "./types";

type Tab = "report" | "dashboard" | "rules" | "admin";
type ReportStep = 0 | 1 | 2;
type DashboardFilter = "all" | "submitted" | "pending" | "late";
type DashboardRow = DashboardPayload["rows"][number];

const EMPTY_REPORT: ReportInput = {
  leadsTotal: 0,
  contacted: 0,
  noAnswer: 0,
  salesA1: 0,
  salesA2: 0,
  salesB1: 0,
  salesB2: 0,
  salesC1: 0,
  salesOnline: 0,
  salesOffline: 0,
  rejectedCount: 0,
  rejectionReason: null,
  extraNote: null,
  tomorrowPlan: null
};

const rejectionReasons = ["Narx", "Vaqt", "Qiziqish past", "Aloqa uzildi", "Boshqa"];

export function App() {
  const [session, setSession] = useState<SessionPayload | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("report");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    let mounted = true;
    getSession()
      .then((payload) => {
        if (!mounted) return;
        setSession(payload);
        setActiveTab(payload.user.role === "seller" ? "report" : "dashboard");
      })
      .catch((requestError: unknown) => {
        if (!mounted) return;
        setError(toApiError(requestError));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const tabs = useMemo(() => buildTabs(session?.user), [session]);

  if (loading) {
    return (
      <main className="app-shell">
        <TopSkeleton />
        <PanelSkeleton />
      </main>
    );
  }

  if (error || !session) {
    return <AccessState error={error} />;
  }

  const refreshSession = async () => {
    const next = await getSession();
    setSession(next);
  };

  return (
    <main className="app-shell">
      <header className="topbar">
        <div>
          <p className="eyebrow">Hisobotchi</p>
          <h1>Kunlik hisobot</h1>
        </div>
        <div className="user-chip" aria-label="Joriy foydalanuvchi">
          <ShieldCheck size={16} />
          <span>{session.user.fullName}</span>
          <strong>{roleLabel(session.user.role)}</strong>
        </div>
      </header>

      <nav className="tabs" aria-label="Asosiy bo'limlar">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={activeTab === tab.id ? "active" : ""}
            onClick={() => setActiveTab(tab.id)}
            title={tab.label}
          >
            <tab.icon size={17} />
            <span>{tab.label}</span>
          </button>
        ))}
      </nav>

      {activeTab === "report" && session.user.role === "seller" ? (
        <ReportForm
          report={session.todayReport}
          today={session.today}
          user={session.user}
          onSaved={async () => {
            await refreshSession();
          }}
        />
      ) : null}

      {activeTab === "dashboard" ? <DashboardView today={session.today} /> : null}

      {activeTab === "rules" ? (
        <RulesView initialRules={session.rules} canEdit={session.user.role === "admin"} />
      ) : null}

      {activeTab === "admin" && session.user.role === "admin" ? <AdminUsers /> : null}
    </main>
  );
}

function ReportForm({
  report,
  today,
  user,
  onSaved
}: {
  report: Report | null;
  today: string;
  user: PublicUser;
  onSaved: () => Promise<void>;
}) {
  const [step, setStep] = useState<ReportStep>(0);
  const [form, setForm] = useState<ReportInput>(reportToForm(report));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const metrics = metricsFromReport(form);
  const stepErrors = validateReportStep(form, step);
  const allErrors = validateReport(form);

  useEffect(() => {
    setForm(reportToForm(report));
  }, [report]);

  const setNumber = (field: keyof ReportInput, value: number) => {
    setFieldErrors((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });
    setForm((current) => ({ ...current, [field]: Math.max(0, Math.trunc(value) || 0) }));
  };

  const setText = (field: keyof ReportInput, value: string | null) => {
    setForm((current) => ({ ...current, [field]: value && value.trim() ? value : null }));
  };

  const nextStep = () => {
    setMessage(null);
    if (stepErrors.length) {
      return;
    }
    setStep((current) => Math.min(2, current + 1) as ReportStep);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setMessage(null);
    setFieldErrors({});
    if (allErrors.length) {
      return;
    }

    setSaving(true);
    try {
      await saveTodayReport(form, Boolean(report));
      setMessage(report ? "Hisobot yangilandi" : "Hisobot yuborildi");
      await onSaved();
    } catch (requestError) {
      const apiError = toApiError(requestError);
      setMessage(apiError.message);
      setFieldErrors(apiError.fields ?? {});
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="workspace-panel report-workspace">
      <form className="report-flow" onSubmit={submit}>
        <div className="report-context">
          <div>
            <span className="context-label">
              <CalendarDays size={15} />
              Sana
            </span>
            <strong>{formatDate(today)}</strong>
          </div>
          <div>
            <span className="context-label">
              <UserRound size={15} />
              Sotuvchi
            </span>
            <strong>{user.fullName}</strong>
          </div>
        </div>

        <StepHeader step={step} metrics={metrics} />

        {message ? (
          <InlineNotice tone={Object.keys(fieldErrors).length || allErrors.length ? "danger" : "success"}>
            {message}
          </InlineNotice>
        ) : null}

        {step === 0 ? (
          <LeadStep form={form} fieldErrors={fieldErrors} setNumber={setNumber} metrics={metrics} />
        ) : null}

        {step === 1 ? (
          <SalesStep form={form} fieldErrors={fieldErrors} setNumber={setNumber} metrics={metrics} />
        ) : null}

        {step === 2 ? (
          <NotesStep
            form={form}
            metrics={metrics}
            fieldErrors={fieldErrors}
            setNumber={setNumber}
            setText={setText}
            validationErrors={allErrors}
          />
        ) : null}

        <div className="sticky-actions">
          {step > 0 ? (
            <button className="secondary-button" type="button" onClick={() => setStep((step - 1) as ReportStep)}>
              <ChevronLeft size={18} />
              <span>Ortga</span>
            </button>
          ) : (
            <span />
          )}

          {step < 2 ? (
            <button className="primary-button" type="button" onClick={nextStep}>
              <span>Davom etish</span>
              <ChevronRight size={18} />
            </button>
          ) : (
            <button className="primary-button" type="submit" disabled={saving || allErrors.length > 0}>
              {report ? <Save size={18} /> : <Send size={18} />}
              <span>{saving ? "Saqlanmoqda" : report ? "Yangilash" : "Hisobotni yuborish"}</span>
            </button>
          )}
        </div>
      </form>
    </section>
  );
}

function StepHeader({ step, metrics }: { step: ReportStep; metrics: ReportMetrics }) {
  const labels = ["Leadlar", "Sotuvlar", "Izoh va reja"];
  return (
    <div className="step-header">
      <div>
        <p className="eyebrow">Qadam {step + 1}/3</p>
        <h2>{labels[step]}</h2>
      </div>
      <div className="mini-metrics">
        <span>{metrics.salesLevelTotal} sotuv</span>
        <span>{formatPercent(metrics.conversionRate)} konversiya</span>
      </div>
      <div className="step-track" aria-label={`Qadam ${step + 1}/3`}>
        {[0, 1, 2].map((item) => (
          <span key={item} className={item <= step ? "active" : ""} />
        ))}
      </div>
    </div>
  );
}

function LeadStep({
  form,
  fieldErrors,
  setNumber,
  metrics
}: {
  form: ReportInput;
  fieldErrors: Record<string, string>;
  setNumber: (field: keyof ReportInput, value: number) => void;
  metrics: ReportMetrics;
}) {
  return (
    <div className="step-content">
      <div className="metric-strip">
        <Metric label="Aloqa" value={formatPercent(metrics.contactRate)} icon={TrendingUp} />
        <Metric label="Javob" value={formatPercent(metrics.answerRate)} icon={CheckCircle2} />
      </div>

      <div className="form-grid three">
        <NumberField
          id="leadsTotal"
          label="Kelgan leadlar"
          value={form.leadsTotal}
          error={fieldErrors.leadsTotal}
          onChange={(value) => setNumber("leadsTotal", value)}
        />
        <NumberField
          id="contacted"
          label="Bog'lanildi"
          value={form.contacted}
          error={fieldErrors.contacted}
          onChange={(value) => setNumber("contacted", value)}
        />
        <NumberField
          id="noAnswer"
          label="Javob bermadi"
          value={form.noAnswer}
          error={fieldErrors.noAnswer}
          onChange={(value) => setNumber("noAnswer", value)}
        />
      </div>

      {validateReportStep(form, 0).map((error) => (
        <InlineNotice key={error} tone="danger">
          {error}
        </InlineNotice>
      ))}
    </div>
  );
}

function SalesStep({
  form,
  fieldErrors,
  setNumber,
  metrics
}: {
  form: ReportInput;
  fieldErrors: Record<string, string>;
  setNumber: (field: keyof ReportInput, value: number) => void;
  metrics: ReportMetrics;
}) {
  return (
    <div className="step-content">
      <div className="total-compare">
        <div>
          <span>Daraja bo'yicha</span>
          <strong>{metrics.salesLevelTotal}</strong>
        </div>
        <div>
          <span>Format bo'yicha</span>
          <strong>{metrics.salesFormatTotal}</strong>
        </div>
      </div>

      <SectionTitle icon={ClipboardCheck} title="Daraja bo'yicha sotuv" />
      <div className="level-grid">
        <NumberField id="salesA1" label="A1" value={form.salesA1} onChange={(value) => setNumber("salesA1", value)} />
        <NumberField id="salesA2" label="A2" value={form.salesA2} onChange={(value) => setNumber("salesA2", value)} />
        <NumberField id="salesB1" label="B1" value={form.salesB1} onChange={(value) => setNumber("salesB1", value)} />
        <NumberField id="salesB2" label="B2" value={form.salesB2} onChange={(value) => setNumber("salesB2", value)} />
        <NumberField id="salesC1" label="C1" value={form.salesC1} onChange={(value) => setNumber("salesC1", value)} />
      </div>

      <SectionTitle icon={CircleDashed} title="Format bo'yicha sotuv" />
      <div className="form-grid two">
        <NumberField
          id="salesOffline"
          label="Offline"
          value={form.salesOffline}
          error={fieldErrors.salesOffline}
          onChange={(value) => setNumber("salesOffline", value)}
        />
        <NumberField
          id="salesOnline"
          label="Online"
          value={form.salesOnline}
          error={fieldErrors.salesOnline}
          onChange={(value) => setNumber("salesOnline", value)}
        />
      </div>

      {validateReportStep(form, 1).map((error) => (
        <InlineNotice key={error} tone="danger">
          {error}
        </InlineNotice>
      ))}
    </div>
  );
}

function NotesStep({
  form,
  metrics,
  fieldErrors,
  setNumber,
  setText,
  validationErrors
}: {
  form: ReportInput;
  metrics: ReportMetrics;
  fieldErrors: Record<string, string>;
  setNumber: (field: keyof ReportInput, value: number) => void;
  setText: (field: keyof ReportInput, value: string | null) => void;
  validationErrors: string[];
}) {
  return (
    <div className="step-content">
      <div className="review-panel">
        <Metric label="Leadlar" value={String(form.leadsTotal)} icon={FileText} />
        <Metric label="Sotuv" value={String(metrics.salesLevelTotal)} icon={CheckCircle2} />
        <Metric label="Konversiya" value={formatPercent(metrics.conversionRate)} icon={TrendingUp} />
      </div>

      {validationErrors.map((error) => (
        <InlineNotice key={error} tone="danger">
          {error}
        </InlineNotice>
      ))}

      <div className="form-grid two">
        <NumberField
          id="rejectedCount"
          label="Rad etganlar soni"
          value={form.rejectedCount}
          error={fieldErrors.rejectedCount}
          onChange={(value) => setNumber("rejectedCount", value)}
        />
        <div className="field-block">
          <span className="field-label-text">Asosiy sabab</span>
          <div className="chip-group" role="group" aria-label="Rad etish sababi">
            {rejectionReasons.map((reason) => (
              <button
                key={reason}
                type="button"
                className={form.rejectionReason === reason ? "chip selected" : "chip"}
                onClick={() => setText("rejectionReason", form.rejectionReason === reason ? null : reason)}
              >
                {reason}
              </button>
            ))}
          </div>
        </div>
      </div>

      <label className="text-field" htmlFor="extraNote">
        Qo'shimcha izoh
        <textarea
          id="extraNote"
          value={form.extraNote ?? ""}
          onChange={(event) => setText("extraNote", event.target.value)}
          rows={3}
          placeholder="Bugun qiziq holat yuz berdi..."
        />
      </label>

      <label className="text-field" htmlFor="tomorrowPlan">
        Ertangi reja
        <textarea
          id="tomorrowPlan"
          value={form.tomorrowPlan ?? ""}
          onChange={(event) => setText("tomorrowPlan", event.target.value)}
          rows={3}
          placeholder="Follow-up qo'ng'iroqlar, yangi leadlarga javob..."
        />
      </label>
    </div>
  );
}

function DashboardView({ today }: { today: string }) {
  const [date, setDate] = useState(today);
  const [filter, setFilter] = useState<DashboardFilter>("all");
  const [dashboard, setDashboard] = useState<DashboardPayload | null>(null);
  const [selectedRow, setSelectedRow] = useState<DashboardRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async (nextDate = date) => {
    setLoading(true);
    setError(null);
    try {
      setDashboard(await getDashboard(nextDate));
      setSelectedRow(null);
    } catch (requestError) {
      setError(toApiError(requestError).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(today);
  }, [today]);

  const rows = dashboard?.rows.filter((row) => matchesDashboardFilter(row.status, filter)) ?? [];

  return (
    <section className="workspace-panel">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">Dashboard</p>
          <h2>{formatDate(date)}</h2>
        </div>
        <div className="inline-controls">
          <input
            aria-label="Dashboard sanasi"
            type="date"
            value={date}
            onChange={(event) => {
              setDate(event.target.value);
              void load(event.target.value);
            }}
          />
          <button type="button" className="icon-button" onClick={() => void load()} title="Yangilash">
            <RefreshCw size={17} />
          </button>
        </div>
      </div>

      {loading ? <DashboardSkeleton /> : null}
      {error ? <InlineNotice tone="danger">{error}</InlineNotice> : null}

      {dashboard && !loading ? (
        <>
          <div className="metric-grid summary">
            <Metric label="Leadlar" value={String(dashboard.totals.leadsTotal)} icon={FileText} />
            <Metric label="Sotuvlar" value={String(dashboard.totals.salesTotal)} icon={CheckCircle2} />
            <Metric label="Konversiya" value={formatPercent(dashboard.totals.conversionRate)} icon={TrendingUp} />
            <Metric label="Topshirmagan" value={String(dashboard.missingCount)} icon={AlertTriangle} />
          </div>

          <div className="filter-chips" role="group" aria-label="Dashboard filterlari">
            {[
              ["all", "Hammasi"],
              ["submitted", "Topshirgan"],
              ["pending", "Kutilmoqda"],
              ["late", "Kechikkan"]
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={filter === id ? "chip selected" : "chip"}
                onClick={() => setFilter(id as DashboardFilter)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="seller-list">
            {rows.map((row) => (
              <button
                type="button"
                className="seller-card clickable"
                key={row.seller.id}
                onClick={() => setSelectedRow(row)}
              >
                <div className="seller-main">
                  <h3>{row.seller.fullName}</h3>
                  <p>
                    {row.report
                      ? `${row.report.leadsTotal} lead / ${row.metrics?.salesLevelTotal ?? 0} sotuv / ${formatPercent(row.metrics?.conversionRate ?? 0)}`
                      : "Hisobot hali yo'q"}
                  </p>
                  {row.report ? <small>Yangilandi: {formatTime(row.report.updatedAt)}</small> : null}
                </div>
                <StatusBadge status={row.status} />
              </button>
            ))}
          </div>

          {selectedRow ? <ReportDetailSheet row={selectedRow} onClose={() => setSelectedRow(null)} /> : null}
        </>
      ) : null}
    </section>
  );
}

function ReportDetailSheet({ row, onClose }: { row: DashboardRow; onClose: () => void }) {
  const { report, metrics, seller } = row;

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <aside
        className="report-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="report-sheet-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="sheet-handle" aria-hidden="true" />
        <div className="sheet-heading">
          <div>
            <p className="eyebrow">Xodim hisoboti</p>
            <h2 id="report-sheet-title">{seller.fullName}</h2>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Yopish">
            <X size={18} />
          </button>
        </div>

        <div className="sheet-status-row">
          <StatusBadge status={row.status} />
          {report ? <span>Yangilandi: {formatTime(report.updatedAt)}</span> : null}
        </div>

        {report && metrics ? (
          <>
            <div className="detail-summary">
              <Metric label="Leadlar" value={String(report.leadsTotal)} icon={FileText} />
              <Metric label="Sotuv" value={String(metrics.salesLevelTotal)} icon={CheckCircle2} />
              <Metric label="Aloqa" value={formatPercent(metrics.contactRate)} icon={TrendingUp} />
              <Metric label="Konversiya" value={formatPercent(metrics.conversionRate)} icon={BarChart3} />
            </div>

            <DetailSection title="Leadlar">
              <DetailGrid
                items={[
                  ["Kelgan leadlar", report.leadsTotal],
                  ["Bog'lanildi", report.contacted],
                  ["Javob bermadi", report.noAnswer],
                  ["Javob darajasi", formatPercent(metrics.answerRate)]
                ]}
              />
            </DetailSection>

            <DetailSection title="Sotuvlar">
              <DetailGrid
                items={[
                  ["A1", report.salesA1],
                  ["A2", report.salesA2],
                  ["B1", report.salesB1],
                  ["B2", report.salesB2],
                  ["C1", report.salesC1],
                  ["Online", report.salesOnline],
                  ["Offline", report.salesOffline],
                  ["Jami", metrics.salesLevelTotal]
                ]}
              />
            </DetailSection>

            <DetailSection title="Rad etganlar">
              <DetailGrid
                items={[
                  ["Soni", report.rejectedCount],
                  ["Asosiy sabab", report.rejectionReason || "Belgilanmagan"]
                ]}
              />
            </DetailSection>

            <DetailSection title="Izoh va reja">
              <div className="note-block">
                <span>Qo'shimcha izoh</span>
                <p>{report.extraNote || "Izoh yozilmagan"}</p>
              </div>
              <div className="note-block">
                <span>Ertangi reja</span>
                <p>{report.tomorrowPlan || "Reja yozilmagan"}</p>
              </div>
            </DetailSection>
          </>
        ) : (
          <div className="empty-report">
            <CircleDashed size={30} />
            <h3>Hisobot hali topshirilmagan</h3>
            <p>Bu xodim tanlangan sana uchun hisobot yubormagan.</p>
          </div>
        )}
      </aside>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="detail-section">
      <h3>{title}</h3>
      {children}
    </section>
  );
}

function DetailGrid({ items }: { items: Array<[string, string | number]> }) {
  return (
    <div className="detail-grid">
      {items.map(([label, value]) => (
        <div key={label}>
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  );
}

function RulesView({ initialRules, canEdit }: { initialRules: string; canEdit: boolean }) {
  const [rules, setRules] = useState(initialRules);
  const [draft, setDraft] = useState(initialRules);
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    getRules().then((payload) => {
      setRules(payload.rules);
      setDraft(payload.rules);
    });
  }, []);

  const save = async () => {
    setMessage(null);
    try {
      const payload = await updateRules(draft);
      setRules(payload.rules);
      setDraft(payload.rules);
      setEditing(false);
      setMessage("Qoidalar saqlandi");
    } catch (requestError) {
      setMessage(toApiError(requestError).message);
    }
  };

  return (
    <section className="workspace-panel">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">Qoidalar</p>
          <h2>Ish tartibi</h2>
        </div>
        {canEdit ? (
          <button className="secondary-button" type="button" onClick={() => setEditing((value) => !value)}>
            <BookOpen size={17} />
            <span>{editing ? "Ko'rish" : "Tahrirlash"}</span>
          </button>
        ) : null}
      </div>

      {editing ? (
        <div className="stack">
          <textarea className="rules-editor" value={draft} onChange={(event) => setDraft(event.target.value)} />
          <button className="primary-button" type="button" onClick={save}>
            <Save size={17} />
            <span>Saqlash</span>
          </button>
        </div>
      ) : (
        <div className="rules-body">{renderRules(rules)}</div>
      )}
      {message ? <InlineNotice tone="success">{message}</InlineNotice> : null}
    </section>
  );
}

function AdminUsers() {
  const [users, setUsers] = useState<PublicUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [form, setForm] = useState({
    telegramId: "",
    fullName: "",
    username: "",
    role: "seller" as Role,
    status: "active" as UserStatus
  });

  const load = async () => {
    setLoading(true);
    const payload = await listUsers();
    setUsers(payload.users);
    setLoading(false);
  };

  useEffect(() => {
    void load();
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setMessage(null);
    try {
      await createUser({ ...form, username: form.username || null });
      setForm({ telegramId: "", fullName: "", username: "", role: "seller", status: "active" });
      await load();
      setMessage("Foydalanuvchi qo'shildi");
    } catch (requestError) {
      setMessage(toApiError(requestError).message);
    }
  };

  const toggleStatus = async (user: PublicUser) => {
    await updateUser(user.id, { status: user.status === "active" ? "inactive" : "active" });
    await load();
  };

  return (
    <section className="workspace-panel">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">Admin</p>
          <h2>Foydalanuvchilar</h2>
        </div>
      </div>

      <form className="admin-form" onSubmit={submit}>
        <input
          aria-label="Telegram ID"
          placeholder="Telegram ID"
          value={form.telegramId}
          onChange={(event) => setForm((current) => ({ ...current, telegramId: event.target.value }))}
        />
        <input
          aria-label="Ism"
          placeholder="Ism"
          value={form.fullName}
          onChange={(event) => setForm((current) => ({ ...current, fullName: event.target.value }))}
        />
        <select
          aria-label="Rol"
          value={form.role}
          onChange={(event) => setForm((current) => ({ ...current, role: event.target.value as Role }))}
        >
          <option value="seller">Sotuvchi</option>
          <option value="supervisor">Nazoratchi</option>
          <option value="admin">Admin</option>
        </select>
        <button className="primary-button" type="submit">
          <Plus size={17} />
          <span>Qo'shish</span>
        </button>
      </form>

      {message ? <InlineNotice tone="success">{message}</InlineNotice> : null}
      {loading ? <PanelSkeleton compact /> : null}
      <div className="seller-list">
        {users.map((user) => (
          <article className="seller-card" key={user.id}>
            <div className="seller-main">
              <h3>{user.fullName}</h3>
              <p>{user.telegramId} - {roleLabel(user.role)}</p>
            </div>
            <button className="secondary-button compact" type="button" onClick={() => void toggleStatus(user)}>
              {user.status === "active" ? "Active" : "Inactive"}
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}

function NumberField({
  id,
  label,
  value,
  error,
  onChange
}: {
  id: string;
  label: string;
  value: number;
  error?: string | undefined;
  onChange: (value: number) => void;
}) {
  return (
    <div className="number-field">
      <label htmlFor={id}>{label}</label>
      <div className="number-control">
        <button type="button" aria-label={`${label} kamaytirish`} onClick={() => onChange(value - 1)}>
          <Minus size={16} />
        </button>
        <input
          id={id}
          inputMode="numeric"
          min={0}
          type="number"
          value={String(value)}
          onChange={(event) => onChange(Number(event.target.value))}
        />
        <button type="button" aria-label={`${label} oshirish`} onClick={() => onChange(value + 1)}>
          <Plus size={16} />
        </button>
      </div>
      {error ? <span className="field-error">{error}</span> : null}
    </div>
  );
}

function SectionTitle({
  icon: Icon,
  title,
  meta
}: {
  icon: typeof FileText;
  title: string;
  meta?: string;
}) {
  return (
    <div className="section-title">
      <span>
        <Icon size={16} />
        {title}
      </span>
      {meta ? <strong>{meta}</strong> : null}
    </div>
  );
}

function Metric({
  label,
  value,
  icon: Icon
}: {
  label: string;
  value: string;
  icon: typeof FileText;
}) {
  return (
    <div className="metric">
      <span>
        <Icon size={15} />
        {label}
      </span>
      <strong>{value}</strong>
    </div>
  );
}

function InlineNotice({ tone, children }: { tone: "danger" | "success" | "neutral"; children: ReactNode }) {
  return <p className={`inline-notice ${tone}`}>{children}</p>;
}

function StatusBadge({ status }: { status: DashboardPayload["rows"][number]["status"] }) {
  const className = `status-badge ${status}`;
  const label =
    status === "submitted"
      ? "Topshirildi"
      : status === "edited"
        ? "Tahrirlandi"
        : status === "late"
          ? "Kechikdi"
          : status === "missing"
            ? "Topshirmagan"
            : "Kutilmoqda";
  return (
    <span className={className}>
      {status === "missing" ? <AlertTriangle size={14} /> : <Clock3 size={14} />}
      {label}
    </span>
  );
}

function AccessState({ error }: { error: ApiError | null }) {
  const pending = error?.code === "access_pending" || error?.code === "missing_auth";
  return (
    <main className="app-shell centered">
      <div className="access-box">
        <ShieldCheck size={34} />
        <h1>{pending ? "Ruxsat kutilmoqda" : "Kirish imkonsiz"}</h1>
        <p>{error?.message ?? "Telegram orqali qayta oching"}</p>
      </div>
    </main>
  );
}

function TopSkeleton() {
  return (
    <div className="topbar">
      <div className="skeleton-block title" />
      <div className="skeleton-block chip" />
    </div>
  );
}

function PanelSkeleton({ compact = false }: { compact?: boolean }) {
  return (
    <section className="workspace-panel skeleton-panel">
      {Array.from({ length: compact ? 2 : 5 }).map((_, index) => (
        <div className="skeleton-line" key={index} />
      ))}
    </section>
  );
}

function DashboardSkeleton() {
  return (
    <>
      <div className="metric-grid summary">
        {Array.from({ length: 4 }).map((_, index) => (
          <div className="metric skeleton-card" key={index} />
        ))}
      </div>
      <PanelSkeleton compact />
    </>
  );
}

function buildTabs(user: PublicUser | undefined) {
  if (!user) return [];
  const tabs: Array<{ id: Tab; label: string; icon: typeof FileText }> = [];
  if (user.role === "seller") tabs.push({ id: "report", label: "Hisobot", icon: FileText });
  if (user.role === "admin" || user.role === "supervisor") {
    tabs.push({ id: "dashboard", label: "Dashboard", icon: BarChart3 });
  }
  tabs.push({ id: "rules", label: "Qoidalar", icon: BookOpen });
  if (user.role === "admin") tabs.push({ id: "admin", label: "Admin", icon: Users });
  return tabs;
}

function roleLabel(role: Role): string {
  if (role === "admin") return "Admin";
  if (role === "supervisor") return "Nazoratchi";
  return "Sotuvchi";
}

function reportToForm(report: Report | null): ReportInput {
  return report ? { ...report } : { ...EMPTY_REPORT };
}

function metricsFromReport(report: ReportInput): ReportMetrics {
  const salesLevelTotal = report.salesA1 + report.salesA2 + report.salesB1 + report.salesB2 + report.salesC1;
  const salesFormatTotal = report.salesOnline + report.salesOffline;
  return {
    salesLevelTotal,
    salesFormatTotal,
    contactRate: rate(report.contacted, report.leadsTotal),
    answerRate: rate(report.contacted, report.contacted + report.noAnswer),
    conversionRate: rate(salesLevelTotal, report.leadsTotal)
  };
}

function validateReportStep(report: ReportInput, step: ReportStep): string[] {
  if (step === 0 && report.contacted + report.noAnswer > report.leadsTotal) {
    return ["Bog'lanildi va javob bermadi yig'indisi leadlardan oshmasin"];
  }
  if (step === 1) {
    const metrics = metricsFromReport(report);
    if (metrics.salesLevelTotal !== metrics.salesFormatTotal) {
      return ["Daraja bo'yicha sotuv jami online/offline jamiga teng bo'lishi kerak"];
    }
  }
  return [];
}

function validateReport(report: ReportInput): string[] {
  return [...validateReportStep(report, 0), ...validateReportStep(report, 1)];
}

function rate(value: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((value / total) * 1000) / 10;
}

function formatPercent(value: number): string {
  return `${value}%`;
}

function formatDate(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : value;
}

function formatTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("uz-UZ", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Tashkent"
  }).format(date);
}

function matchesDashboardFilter(
  status: DashboardPayload["rows"][number]["status"],
  filter: DashboardFilter
): boolean {
  if (filter === "all") return true;
  if (filter === "submitted") return status === "submitted" || status === "edited";
  if (filter === "pending") return status === "pending";
  return status === "late" || status === "missing";
}

function renderRules(rules: string) {
  const listItems: string[] = [];
  const blocks: ReactNode[] = [];

  const flushList = () => {
    if (listItems.length === 0) return;
    blocks.push(
      <ul key={`list-${blocks.length}`}>
        {listItems.splice(0).map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    );
  };

  for (const line of rules.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      continue;
    }
    if (trimmed.startsWith("# ")) {
      flushList();
      blocks.push(<h3 key={`h-${blocks.length}`}>{trimmed.slice(2)}</h3>);
    } else if (trimmed.startsWith("- ")) {
      listItems.push(trimmed.slice(2));
    } else {
      flushList();
      blocks.push(<p key={`p-${blocks.length}`}>{trimmed}</p>);
    }
  }
  flushList();
  return blocks;
}

function toApiError(error: unknown): ApiError {
  if (error instanceof ApiRequestError) return error.apiError;
  return { code: "unknown_error", message: "Kutilmagan xatolik" };
}
